import React from "react";
import { defaultEditorDefns, EditorDefns, TlfViewModeConfig } from "../../utility";
import { ArsListingContent } from "./ArsListingContent";
import { ArsListingContextProvider } from "./ArsListingContextProvider";
import { type ArsListingBody, type ArsListingDataset } from "./interface";

/**
 * Props for the ArsListing component.
 */
export interface ArsListingProps<T> {
  /** ID for the listing component */
  id?: string;
  /** Pre-populated listing body shown on first render. */
  arsListingBody: ArsListingBody<T>;
  /** Clinical datasets available for the listing. */
  datasets: ArsListingDataset[];
  /** Optional flag to disable listing interactions. */
  disabled?: boolean;
  /** Editor props used by cell editors */
  editorProps?: EditorDefns<T>;
  /** Horizontal justification of the scaffold table within its container */
  justification?: "left" | "center" | "right";
  /** Optional sidebar width. */
  sidebarWidth?: string;
  /** TLF view mode controlling how cell data is displayed. */
  tlfViewMode: TlfViewModeConfig;
  /** Callback invoked when the listing body changes */
  updateArsListingBody: (newBody: ArsListingBody<T>) => void;
}

/**
 * ArsListing presents a scaffold table with a variable sidebar. Users can drag one or
 * more variables onto header cells and position them before, after, or in place using
 * nine recognised drop zones (edges, corners, and centre).
 *
 * @param props - Component props
 * @param props.id - ID for the listing component
 * @param props.arsListingBody - Listing body data
 * @param props.datasets - Clinical datasets that provide draggable variables
 * @param props.disabled - Optional flag that disables listing interactions
 * @param props.editorProps - Editor props used by cell editors
 * @param props.justification - Horizontal justification of the scaffold table within its container
 * @param props.sidebarWidth - Optional sidebar width
 * @param props.tlfViewMode - TLF view mode controlling how cell data is displayed
 * @param props.updateArsListingBody - Callback invoked when the listing body changes
 * @returns ArsListing table and draggable variable sidebar
 */
export const ArsListing = <T,>({
  id = "ars-listing",
  arsListingBody,
  datasets,
  disabled = false,
  editorProps = defaultEditorDefns as EditorDefns<T>,
  justification,
  sidebarWidth = "360px",
  tlfViewMode,
  updateArsListingBody,
}: ArsListingProps<T>): React.ReactElement => {
  return (
    <ArsListingContextProvider
      bodyData={arsListingBody}
      updateBodyData={updateArsListingBody}
      settings={{
        id,
        currentDatasetId: datasets[0]?.id ?? "",
        datasets,
        editorProps,
        justification,
        showVariableList: true,
        disabled,
        sidebarWidth,
        tlfViewMode,
        variableFilter: "",
      }}
    >
      <ArsListingContent />
    </ArsListingContextProvider>
  );
};

ArsListing.displayName = "ArsListing";
